// ============================================================
// DEALIN MVP - Auth Store (Zustand)
// ============================================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, UserProfile, VendorCompany, UserContext } from '@/types';
import { getAccessToken, clearTokens } from '@/api/client';
import * as authApi from '@/api/auth';
import { identifyUser, resetUser, captureEvent, PHEvents } from '@/lib/posthog';

// ------------------------------------------------------------
// Types
// ------------------------------------------------------------

interface AuthState {
  // State
  user: User | null;
  profile: UserProfile | null;
  vendor: VendorCompany | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  
  // Computed getters
  isVendor: boolean;
  isClient: boolean;
  unreadMessages: number;
  unreadNotifications: number;
  
  // Actions
  login: (username: string, password: string) => Promise<{ success: boolean; isVendor?: boolean; isClient?: boolean }>;
  loginWithGoogle: (credential: string) => Promise<{ success: boolean; isVendor?: boolean; isClient?: boolean }>;
  logout: () => Promise<void>;
  loadUserContext: () => Promise<void>;
  initialize: () => Promise<void>;
  setUnreadCounts: (messages: number, notifications: number) => void;
  clearError: () => void;
}

// ------------------------------------------------------------
// Store
// ------------------------------------------------------------

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      profile: null,
      vendor: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      error: null,
      isVendor: false,
      isClient: false,
      unreadMessages: 0,
      unreadNotifications: 0,

      // ------------------------------------------------------------
      // Login
      // ------------------------------------------------------------
      login: async (username: string, password: string): Promise<{ success: boolean; isVendor?: boolean; isClient?: boolean }> => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await authApi.login({ username, password });
          
          if (response.success && response.user) {
            const { user, profile, vendor, is_vendor, is_client, unread_messages, unread_notifications } = response.user;
            
            set({
              user,
              profile,
              vendor,
              isAuthenticated: true,
              isVendor: is_vendor,
              isClient: is_client,
              unreadMessages: unread_messages,
              unreadNotifications: unread_notifications,
              isLoading: false,
              error: null,
              isInitialized: true,
            });
            
            if (user) {
              identifyUser(user.id, {
                email: user.email,
                name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
                is_vendor: is_vendor,
                is_client: is_client,
                company: vendor?.name || profile?.company_name || '',
              });
            }
            captureEvent('user_login', { method: 'email', is_vendor: is_vendor });
            PHEvents.login();

            setTimeout(() => {
              get().loadUserContext();
            }, 100);
            
            return { success: true, isVendor: is_vendor, isClient: is_client };
          } else {
            set({
              isLoading: false,
              error: 'Login failed',
            });
            return { success: false };
          }
        } catch (e: unknown) {
          let message = 'Login failed';
          if (e && typeof e === 'object' && 'response' in e) {
            const axiosErr = e as { response?: { data?: { error?: string } } };
            message = axiosErr.response?.data?.error || message;
          } else if (e instanceof Error) {
            message = e.message;
          }
          set({
            isLoading: false,
            error: message,
          });
          return { success: false };
        }
      },

      // ------------------------------------------------------------
      // Google Login
      // ------------------------------------------------------------
      loginWithGoogle: async (credential: string): Promise<{ success: boolean; isVendor?: boolean; isClient?: boolean }> => {
        set({ isLoading: true, error: null });

        try {
          const response = await authApi.googleLogin({ credential });

          if (response.success && response.user) {
            const { user, profile, vendor, is_vendor, is_client, unread_messages, unread_notifications } = response.user;

            set({
              user,
              profile,
              vendor,
              isAuthenticated: true,
              isVendor: is_vendor,
              isClient: is_client,
              unreadMessages: unread_messages,
              unreadNotifications: unread_notifications,
              isLoading: false,
              error: null,
              isInitialized: true,
            });

            if (user) {
              identifyUser(user.id, {
                email: user.email,
                name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
                is_vendor: is_vendor,
                is_client: is_client,
                company: vendor?.name || profile?.company_name || '',
              });
            }
            captureEvent('user_login', { method: 'google', is_vendor: is_vendor });
            PHEvents.login();

            setTimeout(() => {
              get().loadUserContext();
            }, 100);

            return { success: true, isVendor: is_vendor, isClient: is_client };
          } else {
            set({
              isLoading: false,
              error: 'Login con Google fallito',
            });
            return { success: false };
          }
        } catch (e: unknown) {
          let message = 'Login con Google fallito';
          if (e && typeof e === 'object' && 'response' in e) {
            const axiosErr = e as { response?: { data?: { error?: string } } };
            message = axiosErr.response?.data?.error || message;
          } else if (e instanceof Error) {
            message = e.message;
          }
          set({
            isLoading: false,
            error: message,
          });
          return { success: false };
        }
      },

      // ------------------------------------------------------------
      // Logout
      // ------------------------------------------------------------
      logout: async (): Promise<void> => {
        set({ isLoading: true });
        
        try {
          await authApi.logout();
        } catch {
          // Ignore errors
        } finally {
          PHEvents.logout();
          captureEvent('user_logout');
          resetUser();
          clearTokens();
          set({
            user: null,
            profile: null,
            vendor: null,
            isAuthenticated: false,
            isVendor: false,
            isClient: false,
            unreadMessages: 0,
            unreadNotifications: 0,
            isLoading: false,
            error: null,
          });
        }
      },

      // ------------------------------------------------------------
      // Load User Context
      // ------------------------------------------------------------
      loadUserContext: async (): Promise<void> => {
        // Evita chiamate duplicate se già in caricamento
        if (get().isLoading) return;
        
        set({ isLoading: true });
        
        try {
          const context: UserContext = await authApi.getUserContext();
          
          if (context.is_authenticated && context.user) {
            set({
              user: context.user,
              profile: context.profile,
              vendor: context.vendor,
              isAuthenticated: true,
              isVendor: context.is_vendor,
              isClient: context.is_client,
              unreadMessages: context.unread_messages,
              unreadNotifications: context.unread_notifications,
              isLoading: false,
              error: null,
            });
          } else {
            // Not authenticated, clear state
            set({
              user: null,
              profile: null,
              vendor: null,
              isAuthenticated: false,
              isVendor: false,
              isClient: false,
              unreadMessages: 0,
              unreadNotifications: 0,
              isLoading: false,
              error: null,
            });
          }
        } catch {
          // Error loading context, might be not authenticated
          set({
            user: null,
            profile: null,
            vendor: null,
            isAuthenticated: false,
            isVendor: false,
            isClient: false,
            unreadMessages: 0,
            unreadNotifications: 0,
            isLoading: false,
            error: null,
          });
        }
      },

      // ------------------------------------------------------------
      // Initialize (called on app start)
      // ------------------------------------------------------------
      initialize: async (): Promise<void> => {
        if (get().isInitialized) return;
        
        const token = getAccessToken();
        const currentState = get();
        
        // If we have a token OR persisted auth state, verify with backend
        if (token || currentState.isAuthenticated) {
          // Load/refresh user context from backend
          // This will establish Django session if JWT is valid
          await get().loadUserContext();
        }
        
        set({ isInitialized: true });
      },

      // ------------------------------------------------------------
      // Set Unread Counts (for real-time updates)
      // ------------------------------------------------------------
      setUnreadCounts: (messages: number, notifications: number): void => {
        set({
          unreadMessages: messages,
          unreadNotifications: notifications,
        });
      },

      // ------------------------------------------------------------
      // Clear Error
      // ------------------------------------------------------------
      clearError: (): void => {
        set({ error: null });
      },
    }),
    {
      name: 'dealin-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist essential auth state, not loading/error/initialized states
        user: state.user,
        profile: state.profile,
        vendor: state.vendor,
        isAuthenticated: state.isAuthenticated,
        isVendor: state.isVendor,
        isClient: state.isClient,
        // isInitialized is NOT persisted - it should reset on every page load
      }),
    }
  )
);

// ------------------------------------------------------------
// Selector Hooks (for better performance)
// ------------------------------------------------------------

export const useUser = () => useAuthStore((state) => state.user);
export const useProfile = () => useAuthStore((state) => state.profile);
export const useVendor = () => useAuthStore((state) => state.vendor);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useIsVendor = () => useAuthStore((state) => state.isVendor);
export const useIsClient = () => useAuthStore((state) => state.isClient);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);

// ------------------------------------------------------------
// Listen for logout events from API client
// ------------------------------------------------------------

if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    useAuthStore.getState().logout();
  });
}
